import { type InfiniteData, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { type DailyLogSearchErrorKind, getDailyLogSearchErrorKind, isCursorErrorKind } from "./dailyLogSearchErrors";
import { DAILY_LOG_SEARCH_PAGE_SIZE, fetchDailyLogSearch } from "./dailyLogSearchQueries";
import type { DailyLogSearchResponse } from "./dailyLogSearchSchema";
import { type DailyLogSearchConditions, toApiParams } from "./dailyLogSearchState";

/**
 * 여러 페이지 목록의 갱신을 한 경로로 처리한다 (TRD §6.5 coordinator).
 * 목록 복귀·Refresh results·본인 변경 후 갱신·캐시 없는 복귀 복원이 모두 여기를 지난다.
 *
 * 1. 첫 cursor부터 목표 깊이까지(또는 has_more=false까지) 순서대로 조회해 임시로 쌓는다.
 * 2. 전부 성공하고 generation이 그대로일 때만 캐시의 pages/pageParams를 한 번에 교체한다.
 * 3. 중간 실패는 이전 chain을 유지한다. 취소된 generation은 결과를 적용하지 않는다.
 */

export type DailyLogSearchChain = InfiniteData<DailyLogSearchResponse, string | null>;

/** 도중 실패. 이미 받은 페이지를 담아 같은 지점부터 재시도할 수 있게 한다 */
export class DailyLogSearchChainError extends Error {
  constructor(
    readonly cause: unknown,
    readonly partial: DailyLogSearchChain,
  ) {
    super("Failed to rebuild daily log search chain");
  }
}

interface FetchChainParams {
  projectId: number;
  conditions: DailyLogSearchConditions;
  targetPageCount: number;
  signal: AbortSignal;
  limit?: number;
  /** 이전 시도에서 받은 페이지. 마지막 cursor부터 이어서 조회한다 */
  resumeFrom?: DailyLogSearchChain;
}

export async function fetchDailyLogSearchChain({
  projectId,
  conditions,
  targetPageCount,
  signal,
  limit = DAILY_LOG_SEARCH_PAGE_SIZE,
  resumeFrom,
}: FetchChainParams): Promise<DailyLogSearchChain> {
  const pages = [...(resumeFrom?.pages ?? [])];
  const pageParams = [...(resumeFrom?.pageParams ?? [])];
  const pageCount = Math.max(1, targetPageCount);

  while (pages.length < pageCount) {
    const lastPage = pages[pages.length - 1];
    if (lastPage && !lastPage.has_more) break;

    const cursor = lastPage?.next_cursor ?? null;
    try {
      const page = await fetchDailyLogSearch(projectId, toApiParams(conditions, limit, cursor), signal);
      pages.push(page);
      pageParams.push(cursor);
    } catch (error) {
      throw new DailyLogSearchChainError(error, { pages, pageParams });
    }
  }

  return { pages, pageParams };
}

export type ChainRunPurpose = "refresh" | "restore";
export type ChainRunResult = "applied" | "canceled" | "failed";

export interface ChainRefresherState {
  status: "idle" | "running" | "error";
  purpose: ChainRunPurpose | null;
  errorKind: DailyLogSearchErrorKind | null;
}

const IDLE_STATE: ChainRefresherState = { status: "idle", purpose: null, errorKind: null };

interface UseChainRefresherParams {
  queryKey: readonly unknown[];
  projectId: number;
  conditions: DailyLogSearchConditions;
}

interface RunOptions {
  targetPageCount: number;
  /** 캐시 교체 직전. 화면 위치를 보존하려면 여기서 anchor를 잡는다 */
  onBeforeApply?: () => void;
  onApplied?: (chain: DailyLogSearchChain, durationMs: number) => void;
}

export function useDailyLogSearchChainRefresher({ queryKey, projectId, conditions }: UseChainRefresherParams) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<ChainRefresherState>(IDLE_STATE);
  const generationRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const partialRef = useRef<DailyLogSearchChain | undefined>(undefined);

  const queryKeyHash = JSON.stringify(queryKey);
  const latestRef = useRef({ queryKey, projectId, conditions });
  latestRef.current = { queryKey, projectId, conditions };

  // queryKeyHash가 바뀌면(조건·사용자·프로젝트 변경) 진행 중인 복구·갱신을 모두 취소한다.
  // biome-ignore lint/correctness/useExhaustiveDependencies: key 변경 시점에 cleanup을 실행하기 위한 의존성
  useEffect(
    function cancelRunOnConditionChange() {
      return () => {
        generationRef.current += 1;
        abortRef.current?.abort();
        partialRef.current = undefined;
        setState(IDLE_STATE);
      };
    },
    [queryKeyHash],
  );

  const run = useCallback(
    async (purpose: ChainRunPurpose, { targetPageCount, onBeforeApply, onApplied }: RunOptions) => {
      const generation = ++generationRef.current;
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const { queryKey: runKey, projectId: runProjectId, conditions: runConditions } = latestRef.current;
      const isStale = () => generation !== generationRef.current || controller.signal.aborted;
      const startedAt = performance.now();

      setState({ status: "running", purpose, errorKind: null });
      // 같은 key의 기본 조회·추가 로딩과 겹치지 않게 한다.
      await queryClient.cancelQueries({ queryKey: runKey, exact: true });

      try {
        const chain = await fetchDailyLogSearchChain({
          projectId: runProjectId,
          conditions: runConditions,
          targetPageCount,
          signal: controller.signal,
          resumeFrom: purpose === "restore" ? partialRef.current : undefined,
        });
        if (isStale()) return "canceled" satisfies ChainRunResult;

        partialRef.current = undefined;
        onBeforeApply?.();
        queryClient.setQueryData<DailyLogSearchChain>(runKey, chain);
        setState(IDLE_STATE);
        onApplied?.(chain, performance.now() - startedAt);
        return "applied" satisfies ChainRunResult;
      } catch (error) {
        if (isStale()) return "canceled" satisfies ChainRunResult;

        const cause = error instanceof DailyLogSearchChainError ? error.cause : error;
        const errorKind = getDailyLogSearchErrorKind(cause);
        // 만료·무효 cursor로는 이어서 조회할 수 없다. 받은 페이지를 버려 재시도가 첫 cursor부터 다시 만들게 한다.
        const canResume = error instanceof DailyLogSearchChainError && !isCursorErrorKind(errorKind);
        partialRef.current = purpose === "restore" && canResume ? error.partial : undefined;
        if (errorKind === "canceled") {
          setState(IDLE_STATE);
          return "canceled" satisfies ChainRunResult;
        }
        setState({ status: "error", purpose, errorKind });
        return "failed" satisfies ChainRunResult;
      }
    },
    [queryClient],
  );

  const dismissError = useCallback(() => {
    partialRef.current = undefined;
    setState(IDLE_STATE);
  }, []);

  return { state, run, dismissError };
}

/* ------------------------------------------------------------------ */
/* 현재 화면의 목록 갱신 요청                                             */
/* ------------------------------------------------------------------ */

type ActiveRefresh = () => Promise<unknown>;

/** 화면에 떠 있는 프로젝트별 검색 목록. 동시에 하나만 활성화된다 */
const activeRefreshers = new Map<number, ActiveRefresh>();

export function registerActiveDailyLogSearch(projectId: number, refresh: ActiveRefresh) {
  activeRefreshers.set(projectId, refresh);
  return () => {
    if (activeRefreshers.get(projectId) === refresh) activeRefreshers.delete(projectId);
  };
}

/**
 * 이 프로젝트의 검색 목록이 화면에 있으면 coordinator로 한 번 갱신한다.
 * 화면에 없으면(상세·편집 중) 아무것도 하지 않는다. 목록 복귀 시 갱신된다.
 */
export async function refreshActiveDailyLogSearch(projectId: number | string): Promise<void> {
  await activeRefreshers.get(Number(projectId))?.();
}
